// Booking duration — THE OPTION LIST IS AN INPUT CONVENIENCE, NEVER A WHITELIST.
//
// THE DEFECT THIS EXISTS TO KILL (PR-000057, Ben 2026-09-03/04). Both BookingForm and
// SeriesActionModal used to declare their own `DURATIONS` list of formatted hour STRINGS
// (['0.5','1.0',...,'4.0']) and apply it to data that already existed:
//
//     const durHrs = (editing.activity_mins / 60).toFixed(1);   // 300 → '5.0', 105 → '1.8'
//     duration: DURATIONS.includes(durHrs) ? durHrs : '1.0';    // not on the list → ONE HOUR
//
// A same-day booking opens in duration mode, so a 5 hour booking opened showing 1.0 and the next
// save — for any unrelated reason — wrote 60 minutes over it. Created correctly, displayed
// incorrectly, saved as displayed. Drag-resize snaps to a 15 minute grid (MIN_DURATION_MS,
// DayView.tsx) and writes activity_mins, so HALF of every drag outcome fed that path in prod.
//
// ⚠ THE RULE, and it is Ben's: "the system needs to work correctly and stick with what is booked,
// not with what it displays." An off-list duration is INJECTED as an extra option for that form
// instance. It is NEVER substituted. Do not reintroduce a membership test that can discard a
// value — that is the bug, in whatever new spelling.
//
// ⚠ MINUTES, AS A NUMBER. The root cause was a toFixed(1) string matched against strings. Holding
// minutes as a number removes the rounding, the formatting and the string compare together. Do not
// route this back through hours or through a string.
//
// ⚠ THE LIST STOPS AT 4:00 BY DECISION, NOT BY OVERSIGHT. Extending it was DECLINED (Ben,
// 2026-09-03): longer bookings are made in Finish mode, which is unchanged. Injection is what
// makes those display and survive; it is not a route to lengthening the list.

/**
 * The dropdown's standard options, in minutes, on a 15 minute grid up to 4:00.
 *
 * ⚠ 15 minutes, not 30. Drag-resize snaps to a 15 minute grid, so a 30 minute grid would leave
 * half of every drag outcome unrepresentable — which is the defect, rebuilt.
 */
export const DURATION_OPTION_MINS: readonly number[] = [
  15, 30, 45, 60, 75, 90, 105, 120, 135, 150, 165, 180, 195, 210, 225, 240,
];

/**
 * LAST-RESORT fallback length for a NEW booking only — never for one that already has a duration.
 *
 * ⚠ THIS IS NO LONGER THE HOUSE DEFAULT. `CompanySettings.default_booking_mins` is (0.12.4);
 * this is what applies when that setting cannot be read — a client running off an offline cache
 * that has never held a settings singleton, or a reference fetch that failed. Call sites pass
 * `settings?.default_booking_mins ?? DEFAULT_DURATION_MINS`; they must never assume `settings`.
 */
export const DEFAULT_DURATION_MINS = 60;

/**
 * The duration a form opens with, in minutes. Takes the booking's real length verbatim; the
 * fallback applies only when there is no length to take (a fresh create, or a null activity_mins).
 *
 * ⚠ This must never consult DURATION_OPTION_MINS. That test is the defect.
 *
 * ⚠ `fallbackMins` IS A PARAMETER, NOT THE MODULE CONSTANT, because the default is now per-tenant
 * (`CompanySettings.default_booking_mins`). It is itself validated: a settings row carrying null,
 * 0 or a non-finite value must not open a zero-length form, so an unusable fallback degrades to
 * DEFAULT_DURATION_MINS rather than propagating into the booking.
 */
export function initialDurationMins(activityMins?: number | null, fallbackMins?: number | null): number {
  if (typeof activityMins === 'number' && Number.isFinite(activityMins) && activityMins > 0) {
    return Math.round(activityMins);
  }
  return typeof fallbackMins === 'number' && Number.isFinite(fallbackMins) && fallbackMins > 0
    ? Math.round(fallbackMins)
    : DEFAULT_DURATION_MINS;
}

/**
 * The options for ONE form instance: the standard grid, plus `currentMins` injected in order when
 * it is off the grid (300 → …225, 240, 300; 47 → …45, 47, 60). Nothing is ever coerced or dropped.
 */
export function durationOptions(currentMins: number): number[] {
  const base = [...DURATION_OPTION_MINS];
  if (!Number.isFinite(currentMins) || currentMins <= 0) return base;
  const mins = Math.round(currentMins);
  return base.includes(mins) ? base : [...base, mins].sort((a, b) => a - b);
}

/**
 * Option label as time, not decimal hours: 105 → "1:45", 300 → "5:00". "1.75 hrs" is unnatural in
 * an aviation context and reintroduces exactly the decimal formatting this module removes.
 */
export function formatDurationLabel(mins: number): string {
  const m = Math.max(0, Math.round(mins));
  return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;
}
